import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineItemProvisioningState } from '../types/order';
import api from '../services/api';

const SPEED_PLANS = [
  { groupname: 'supa-25-10',     label: '25 / 10 Mbps' },
  { groupname: 'supa-25-25',     label: '25 / 25 Mbps' },
  { groupname: 'supa-50-20',     label: '50 / 20 Mbps' },
  { groupname: 'supa-50-50',     label: '50 / 50 Mbps' },
  { groupname: 'supa-100-20',    label: '100 / 20 Mbps' },
  { groupname: 'supa-250-25',    label: '250 / 25 Mbps' },
  { groupname: 'supa-500-500',   label: '500 / 500 Mbps' },
  { groupname: 'supa-750-750',   label: '750 / 750 Mbps' },
  { groupname: 'supa-1000-1000', label: '1000 / 1000 Mbps' },
  { groupname: 'supa-2000-2000', label: '2000 / 2000 Mbps' },
  { groupname: 'supa-3000-3000', label: '3000 / 3000 Mbps' },
];

function generatePassword(length = 16): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr).map((b) => chars[b % chars.length]).join('');
}

type Props = {
  open: boolean;
  orderId: string;
  cartLineIndex: number;
  accountNumber?: string | null;
  existingUsername?: string | null;
  defaultIPoEUsername?: string | null;
  onClose: () => void;
  onSuccess: (updated: LineItemProvisioningState) => void;
};

export default function RadiusCredentialsModal({
  open,
  orderId,
  cartLineIndex,
  accountNumber,
  existingUsername,
  defaultIPoEUsername,
  onClose,
  onSuccess,
}: Props) {
  const isEdit = !!existingUsername;

  const [loadingExisting, setLoadingExisting] = useState(false);
  const [connectionType, setConnectionType] = useState<'ipoe' | 'pppoe'>('ipoe');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [groupname, setGroupname] = useState('supa-100-20');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Reset and populate on open
  useEffect(() => {
    if (!open) return;
    setError(null);
    setCopied(false);
    setPassword(generatePassword());

    if (isEdit && existingUsername) {
      setLoadingExisting(true);
      setUsername(existingUsername);
      setConnectionType('ipoe');
      setGroupname('supa-100-20');
      api.adminGetRadiusSubscriber(existingUsername)
        .then((info) => {
          setConnectionType(info.connection_type);
          setGroupname(info.groupname ?? 'supa-100-20');
        })
        .catch((err: any) => {
          setError(
            err.response?.data?.detail ||
            'Could not load current RADIUS subscriber info — verify values before saving.'
          );
        })
        .finally(() => setLoadingExisting(false));
    } else {
      setConnectionType('ipoe');
      setUsername(defaultIPoEUsername || '');
      setGroupname('supa-100-20');
      setLoadingExisting(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill PPPoE username from account number when switching type (new mode only)
  useEffect(() => {
    if (isEdit) return;
    if (connectionType === 'pppoe' && accountNumber) {
      setUsername(`${accountNumber}@nsw.supa.sonicnet.net.au`);
    } else if (connectionType === 'ipoe') {
      setUsername('');
    }
  }, [connectionType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const updated = await api.adminProvisionRadius({
        order_id: orderId,
        cart_line_index: cartLineIndex,
        connection_type: connectionType,
        username: username.trim(),
        password: connectionType === 'pppoe' ? password : undefined,
        groupname,
        existing_username: isEdit ? existingUsername! : undefined,
      });
      onSuccess(updated);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to provision RADIUS credentials');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = username.trim().length > 0 && groupname && !submitting && !loadingExisting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isEdit ? 'Edit RADIUS Credentials' : 'Add RADIUS Credentials'}
      </DialogTitle>
      <DialogContent>
        {loadingExisting ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Connection type
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={connectionType}
                onChange={(_, v) => v && setConnectionType(v)}
                size="small"
              >
                <ToggleButton value="ipoe">IPoE</ToggleButton>
                <ToggleButton value="pppoe">PPPoE</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Divider />

            {connectionType === 'ipoe' ? (
              <TextField
                label="SAS Identifier (remote-id)"
                placeholder="SASXXXXXXXXX"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                size="small"
                helperText="Format: SAS followed by 10 digits, e.g. SAS0001234567"
              />
            ) : (
              <>
                <TextField
                  label="PPPoE Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Format: {account}@{state}.{carrier}.sonicnet.net.au"
                />
                <TextField
                  label={isEdit ? 'New Password (leave blank to keep current)' : 'Password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? 'Copied!' : 'Copy password'}>
                          <IconButton size="small" onClick={handleCopy}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Generate new password">
                          <IconButton size="small" onClick={() => setPassword(generatePassword())}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Speed plan</InputLabel>
              <Select
                value={groupname}
                label="Speed plan"
                onChange={(e) => setGroupname(e.target.value)}
              >
                {SPEED_PLANS.map((p) => (
                  <MenuItem key={p.groupname} value={p.groupname}>
                    {p.label}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({p.groupname})
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? (isEdit ? 'Updating…' : 'Provisioning…') : (isEdit ? 'Update' : 'Provision')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
